import React from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatValue } from '../utils/formatters';

const COLORS = ['#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#858796', '#5a5c69', '#4e73df', '#fd7e14', '#6f42c1', '#20c9a6'];

const RankedListRenderer = ({ data, config, view, sortBy, sortOrder, onSortChange }) => {
  if (!data) return null;

  const { dataMapping = {}, limit = 10, sorting } = config;
  const items = data[dataMapping.items] || [];

  const sorted = [...items].sort((a, b) => {
    const field = sortBy || sorting?.defaultField || dataMapping.valueField;
    if (!field) return 0;
    const aVal = a[field];
    const bVal = b[field];
    if (typeof aVal === 'string') {
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  }).slice(0, limit);

  const nameField = dataMapping.nameField || 'name';
  const valueField = dataMapping.valueField || 'value';
  const secondaryField = dataMapping.secondaryField;
  const trendField = dataMapping.trendField;
  const valueFormat = dataMapping.valueFormat || 'currency';
  const secondaryFormat = dataMapping.secondaryFormat || 'number';

  if (sorted.length === 0) {
    return (
      <div className="text-center text-gray-500 p-8">
        <p>No data available for this period</p>
      </div>
    );
  }

  if (view === 'bar') {
    const chartData = sorted.map(item => ({
      name: String(item[nameField] || '').length > 15
        ? String(item[nameField]).substring(0, 15) + '...'
        : String(item[nameField] || ''),
      value: item[valueField] || 0,
    }));

    return (
      <div style={{ height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} fontSize={10} />
            <YAxis tickFormatter={(v) => formatValue(v, valueFormat)} fontSize={10} />
            <Tooltip formatter={(v) => [formatValue(v, valueFormat), '']} />
            <Bar dataKey="value" fill="#1cc88a" barSize={30} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (view === 'pie') {
    const pieData = sorted.map((item, i) => ({
      name: String(item[nameField] || ''),
      value: item[valueField] || 0,
      fill: COLORS[i % COLORS.length],
    }));

    return (
      <div style={{ height: '300px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              dataKey="value"
            >
              {pieData.map((entry, i) => (
                <Cell key={i} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip formatter={(v) => formatValue(v, valueFormat)} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div>
      {sorting?.options && (
        <div className="flex items-center gap-1 mb-2">
          <span className="text-xs text-gray-500">Sort:</span>
          <select
            value={sortBy || sorting.defaultField}
            onChange={(e) => onSortChange?.(e.target.value)}
            className="text-xs border rounded px-1 py-0.5"
          >
            {sorting.options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}
      <div className="space-y-1">
        {sorted.map((item, index) => {
          const trend = trendField ? item[trendField] : null;
          const trendNum = trend !== null ? parseFloat(trend) : null;
          return (
            <div key={index} className="bg-white border rounded p-2 hover:bg-gray-50">
              <div className="flex justify-between items-center text-xs">
                <div className="font-medium text-gray-900 truncate flex-1" title={String(item[nameField] || '')}>
                  {item[nameField]}
                </div>
                <div className="flex items-center space-x-2 ml-2">
                  <span className="font-bold text-gray-900">
                    {formatValue(item[valueField], valueFormat)}
                  </span>
                  {secondaryField && (
                    <span className="text-gray-600">
                      {formatValue(item[secondaryField], secondaryFormat)}
                    </span>
                  )}
                  {trendNum !== null && !isNaN(trendNum) && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                      trendNum >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {trendNum >= 0 ? '↑' : '↓'} {Math.abs(trendNum).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {dataMapping.summaryFields && (
        <div className="border-t mt-2 pt-2 flex justify-between items-center text-xs">
          <span className="font-medium text-gray-700">{dataMapping.summaryFields.label || 'Total'}</span>
          <div className="flex items-center space-x-2">
            {dataMapping.summaryFields.fields.map((sf, i) => (
              <span key={i} className="text-gray-600">
                {sf.label && <span className="mr-1">{sf.label}:</span>}
                {formatValue(data[sf.key], sf.format || 'number')}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RankedListRenderer;
